import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyReferrals } from "../api/referrals";
import { useTranslation } from "../context/TranslationContext";
import Avatar from "../components/ds/Avatar";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import Page from "../components/ds/Page";
import PageHeader from "../components/ds/PageHeader";
import StatCell from "../components/ds/StatCell";

/**
 * The signed-in user's invite link, and what inviting people has earned them.
 *
 * Whoever signs up through the link is theirs for good: on everything that person earns
 * on Mintly, the inviter is paid a share of Mintly's fee. The share is set by an admin,
 * and the page says what it is right now.
 *
 * @see mintly-back/src/services/referralService
 */

const money = (amount) => `₾${Number(amount || 0).toFixed(2)}`;
const day = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "";
};

export default function ReferralsPage({ setToast }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    getMyReferrals()
      .then((res) => active && setData(res?.data || null))
      .catch((err) => active && setToast?.(err?.message || t("referrals.loadFailed")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [setToast, t]);

  const copy = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setToast?.(t("common.linkCopied") || "Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setToast?.(t("common.copyFailed") || "Could not copy link");
    }
  };

  const share = async () => {
    if (!data?.link) return;
    try {
      await navigator.share({ title: "Mintly", text: t("referrals.shareText"), url: data.link });
    } catch (e) {
      if (e?.name !== "AbortError") copy();
    }
  };
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const percent = Number(data?.percent || 0);
  const on = percent > 0;
  const surface = "flex flex-col rounded-radius-lg bg-bg-raised p-space-16";

  return (
    <Page>
      <PageHeader title={t("referrals.title")} subtitle={on ? t("referrals.subtitle", { percent }) : t("referrals.subtitleOff")} />

      {loading ? (
        <p className="text-subheadline text-label-secondary">{t("common.loading")}</p>
      ) : !data ? null : (
        <>
          <section className={`${surface} gap-space-12`}>
            <h2 className="text-headline text-label-primary">{t("referrals.yourLink")}</h2>
            <div className="flex flex-col gap-space-8 min-[700px]:flex-row min-[700px]:items-center">
              {/* Wrapped, never cut off: a link that ends at "http://localhos" can't be
                  checked before it's sent. */}
              <div className="flex min-h-[44px] min-w-0 flex-1 items-center gap-space-8 rounded-[22px] bg-fill-tertiary px-space-16 py-space-8">
                <Icon name="link" size={20} className="shrink-0 text-label-tertiary" />
                <p id="invite-link" className="min-w-0 select-all break-all text-callout text-label-secondary">
                  {data.link}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-space-8">
                <Button size="m" icon={<Icon name={copied ? "check" : "copy"} size={20} />} onClick={copy} className="flex-1">
                  {copied ? t("referrals.copied") : t("referrals.copy")}
                </Button>
                {canShare && (
                  <IconButton label={t("referrals.share")} onClick={share}>
                    <Icon name="share" size={22} />
                  </IconButton>
                )}
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-2 min-[700px]:gap-space-24">
            <StatCell label={t("referrals.invited")} value={data.invitedCount || 0} />
            <StatCell label={t("referrals.earned")} value={money(data.earned)} />
          </div>

          <section className={`${surface} gap-space-16`}>
            <h2 className="text-title-3 text-label-primary">{t("referrals.howTitle")}</h2>
            <ol className="flex flex-col gap-space-16">
              {[
                t("referrals.howShare"),
                t("referrals.howSignUp"),
                on ? t("referrals.howEarn", { percent }) : t("referrals.howEarnOff"),
              ].map((step, i) => (
                <li key={i} className="flex gap-space-12">
                  <span className="flex h-[20px] w-[28px] shrink-0 items-center justify-center rounded-full bg-accent-tinted text-footnote-semibold text-accent-text">
                    {i + 1}
                  </span>
                  <p className="min-w-0 text-subheadline text-label-primary">{step}</p>
                </li>
              ))}
            </ol>
            <p className="text-footnote text-label-secondary">{t("referrals.fineprint")}</p>
          </section>

          <section className={`${surface} gap-space-12`}>
            <h2 className="text-title-3 text-label-primary">{t("referrals.peopleTitle")}</h2>
            {data.people?.length ? (
              <ul className="flex flex-col">
                {data.people.map((p) => (
                  <li key={p.id} className="flex items-center gap-space-12 border-b border-separator-default py-space-12 last:border-b-0">
                    <button type="button" onClick={() => navigate(`/profile/${p.id}`)} className="shrink-0 rounded-14" aria-label={p.username}>
                      <Avatar size="m" src={p.avatar} name={p.username} />
                    </button>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <p className="truncate text-subheadline-semibold text-label-primary">{p.businessName || p.username}</p>
                      <p className="text-footnote text-label-tertiary">{t("referrals.joined", { date: day(p.joinedAt) })}</p>
                    </div>
                    <p className={`shrink-0 text-subheadline-semibold ${p.earned > 0 ? "text-accent-text" : "text-label-secondary"}`}>
                      {money(p.earned)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon="users" title={t("referrals.empty")} />
            )}
          </section>
        </>
      )}
    </Page>
  );
}
